import { Item } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  A3InnuvaAgreementCodeDocument,
  A3innuvaContractsAgreementCodeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpanishContractData } from 'modules/contracts/hooks/useFetchSpanishContractData'
import { translate } from 'modules/contracts/lib/translate'
import {
  agreementCodesToOptions,
  codeableFullCode,
  fullName,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'

interface Props {
  form: Form
  translations: { [key: string]: string }
  spanishContractData: SpanishContractData
  legalEntityId: string
}

const AgreementCode = ({ form, translations, spanishContractData, legalEntityId }: Props) => {
  const session = useCurrentSession()
  const agreementCodeId = form.get('es_agreement_code_id').value
  const agreementCodeCode = form.get('es_agreement_code_payroll_integration_code').value
  let agreementCodeName = form.get('es_agreement_code_payroll_integration_name').value

  const initialAgreementCode = agreementCodeId
    ? spanishContractData.agreementCodes.get(String(agreementCodeId))
    : null

  agreementCodeName = agreementCodeName
    ? agreementCodeName
    : agreementCodeCode
      ? initialAgreementCode?.attributes.name
      : null
  const agreementFullCode =
    agreementCodeCode && agreementCodeName
      ? codeableFullCode(agreementCodeCode, agreementCodeName)
      : null

  const { data: agreementCodesData, loading } = useQuery(A3InnuvaAgreementCodeDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      legalEntityId,
    },
  })
  const a3innuvaAgreementCodes =
    agreementCodesData?.payrollIntegrationsA3innuva.agreementCodes ?? []

  const setCurrentAgreementCode = (agreementCodes: A3innuvaContractsAgreementCodeFragment[]) => {
    const currentAgreementCode = agreementCodes.find(({ code, name }) => {
      if (agreementFullCode) {
        return agreementFullCode === codeableFullCode(code, name)
      }
      return initialAgreementCode?.attributes.name === name
    })

    if (currentAgreementCode) {
      form.setValues({
        es_agreement_code_payroll_integration_code: currentAgreementCode.code,
        es_agreement_code_payroll_integration_name: currentAgreementCode.name,
      })
    }
  }

  const onAgreementCodeChanged = (item: Item) => {
    const selectedAgreementCode = a3innuvaAgreementCodes.find(
      ({ code, name }) => item.value === codeableFullCode(code, name)
    )

    form.setValues({
      es_agreement_code_payroll_integration_name: selectedAgreementCode?.name,
      es_agreement_code_payroll_integration_code: selectedAgreementCode?.code,
      es_agreement_code_id: undefined,
    })
  }

  const isFormInitialized = () => {
    if (!form.has('es_agreement_code_payroll_integration_name')) return false
    return form.get('es_agreement_code_payroll_integration_name') === agreementCodeCode
  }

  if (!loading && !isFormInitialized()) {
    setCurrentAgreementCode(a3innuvaAgreementCodes)
  }

  const options = agreementCodesToOptions(a3innuvaAgreementCodes)

  const labelNotFound = () => {
    return options.length
      ? i18n.t(
          'contract_versions.forms.contract_extra_information.a3innuva.agreement_code_not_found'
        )
      : i18n.t('contract_versions.forms.contract_extra_information.a3innuva.label_not_found')
  }

  return (
    <DropdownSearchBar
      name={fullName(agreementCodeCode, agreementCodeName)}
      label={translate(translations, 'agreement_code') || ''}
      onSelect={onAgreementCodeChanged}
      labelNotFound={labelNotFound()}
      data={options}
      searchLabel={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
      )}
      loading={loading}
      popoverWidth="s400"
      ellipsis={false}
    />
  )
}

export default observer(AgreementCode)
